import { api } from '@hit-app/convex';
import {
  appendDismissedReminderKey,
  getReminderKey,
  parseDismissedReminderKeys,
  pickNextReminder,
} from '@hit-app/convex/reminderState';
import { useQuery } from 'convex/react';
import { useState } from 'react';
import { posthogClient } from './posthog';

// Bereits geschlossene Event-Erinnerungen (Event-ID + Starttermin). Bleibt pro Browser erhalten, kein Server-Call.
const DISMISSED_STORAGE_KEY = 'hit-event-reminders-dismissed';

export interface UpcomingEventReminder {
  eventId: string;
  name: string;
  address?: string;
  startDate: number;
  endDate: number;
}

function readDismissedReminderKeys(): string[] {
  try {
    const raw = window.localStorage.getItem(DISMISSED_STORAGE_KEY);
    return raw ? parseDismissedReminderKeys(JSON.parse(raw)) : [];
  } catch {
    // localStorage kann im privaten Modus oder bei blockierten Cookies fehlen
    return [];
  }
}

function writeDismissedReminderKeys(reminderKeys: string[]) {
  try {
    window.localStorage.setItem(DISMISSED_STORAGE_KEY, JSON.stringify(reminderKeys));
  } catch {
    // Ohne Storage erscheint der Dialog beim nächsten Start erneut; das ist akzeptabel
  }
}

/**
 * Liefert das nächste Event innerhalb der nächsten 24h, für das das Mitglied zugesagt hat
 * und dessen Erinnerung noch nicht geschlossen wurde. Jeder Event-Termin wird nur einmal angezeigt.
 */
export function useEventReminderPopup(enabled: boolean) {
  const reminders = useQuery(api.eventReminders.listUpcomingForCurrentMember, enabled ? {} : 'skip');
  const [dismissedReminderKeys, setDismissedReminderKeys] = useState<string[]>(readDismissedReminderKeys);
  const reminder: UpcomingEventReminder | null = pickNextReminder(reminders, dismissedReminderKeys);

  const dismiss = (action: 'dismissed' | 'opened') => {
    if (!reminder) return;
    const next = appendDismissedReminderKey(dismissedReminderKeys, getReminderKey(reminder));
    writeDismissedReminderKeys(next);
    setDismissedReminderKeys(next);
    posthogClient.capture('event_reminder_popup_closed', {
      event_id: reminder.eventId,
      action,
      platform: 'web',
    });
  };

  return { reminder, dismiss };
}
